import { useEffect, useState, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { apiUrl } from 'src/config';

const SOCKET_URL = apiUrl;
const SOCKET_OPTIONS = {
  transports: ['polling', 'websocket'],
  upgrade: true,
  reconnection: true,
  reconnectionAttempts: Infinity,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 5000,
};

export const useWebSocket = () => {
  const [isConnected, setIsConnected] = useState(false);
  const subscribedChannels = useRef<Set<string>>(new Set());
  const socketRef = useRef<Socket | null>(null);

  const onConnect = () => setIsConnected(true);

  const onDisconnect = () => {
    setIsConnected(false);
    subscribedChannels.current.clear();
  };

  const subscribe = useCallback(
    (channel: string, callback: (data: any) => void) => {
      const currentSocket = socketRef.current;
      if (!currentSocket) return;

      if (subscribedChannels.current.has(channel)) {
        console.warn(`Already subscribed to ${channel}`);
        return;
      }

      subscribedChannels.current.add(channel);
      currentSocket.emit('subscribe', { channel });
      currentSocket.on(channel, callback);
    },
    []
  );

  const unsubscribe = useCallback((channel: string) => {
    const currentSocket = socketRef.current;
    if (!currentSocket) return;

    subscribedChannels.current.delete(channel);
    currentSocket.emit('unsubscribe', { channel });
    currentSocket.off(channel);
  }, []);

  useEffect(() => {
    const newSocket = io(SOCKET_URL, SOCKET_OPTIONS);

    newSocket.on('connect', onConnect);
    newSocket.on('disconnect', onDisconnect);

    socketRef.current = newSocket;

    return () => {
      newSocket.disconnect();
      socketRef.current = null;
      subscribedChannels.current.clear();
    };
  }, []);

  return { socket: socketRef.current, isConnected, subscribe, unsubscribe };
};
